import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import toast from 'react-hot-toast'
import { mockService } from '../services/mockService'

/**
 * App-level render tracking.
 *
 * The editor used to poll a render job and alert on completion from inside the
 * MockEditorPage component. That meant navigating away (or refreshing) silenced
 * the alert. This provider lifts tracking above the router: it persists active
 * jobs to localStorage, polls them on an interval regardless of the current
 * route, and owns the single terminal alert (toast + browser notification) for
 * every job — so a user can start a render, go do other work, and still be told
 * when it finishes or fails, and which error if it failed.
 */

const STORAGE_KEY = 'bms_active_renders_v1'
const POLL_INTERVAL_MS = 2000
// Safety valve: stop tracking a job that never reaches a terminal state, so a
// lost/expired job does not poll forever across future sessions.
const MAX_TRACK_MS = 60 * 60 * 1000 // 1 hour
// Shared id of the in-progress "Render in progress" loading toast the editor
// raises. We dismiss it on terminal so it never gets orphaned after navigation.
const PROGRESS_TOAST_ID = 'render'

export interface TrackedRender {
  jobId: string
  mockId: string
  mockTitle: string
  startedAt: number // epoch ms
}

interface RenderNotificationsValue {
  /** Register a freshly enqueued render so it is alerted on from anywhere. */
  trackRender: (render: { jobId: string; mockId: string; mockTitle?: string }) => void
  /** Jobs currently being tracked (e.g. to show a global "rendering" badge). */
  activeRenders: TrackedRender[]
}

const RenderNotificationsContext = createContext<RenderNotificationsValue | null>(null)

function loadActive(): TrackedRender[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((r) => r && typeof r.jobId === 'string' && typeof r.mockId === 'string')
  } catch {
    return []
  }
}

function saveActive(renders: TrackedRender[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(renders))
  } catch {
    // ignore quota / unavailable storage
  }
}

function showBrowserNotification(title: string, body: string) {
  if (!('Notification' in window)) return
  const fire = () => {
    try {
      new Notification(title, { body, icon: '/favicon.ico' })
    } catch {
      // some browsers throw if constructed outside a SW context — ignore
    }
  }
  if (Notification.permission === 'granted') {
    fire()
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((p) => {
      if (p === 'granted') fire()
    })
  }
}

export function RenderNotificationsProvider({ children }: { children: ReactNode }) {
  const [activeRenders, setActiveRenders] = useState<TrackedRender[]>(() => loadActive())
  // Keep a ref in sync so the polling interval always reads the latest list
  // without being torn down and rebuilt on every change.
  const activeRef = useRef<TrackedRender[]>(activeRenders)
  activeRef.current = activeRenders
  // Prevent overlapping polls of the same job when one request runs long.
  const inFlight = useRef<Set<string>>(new Set())

  const setAndPersist = useCallback((updater: (prev: TrackedRender[]) => TrackedRender[]) => {
    setActiveRenders((prev) => {
      const next = updater(prev)
      saveActive(next)
      return next
    })
  }, [])

  const remove = useCallback(
    (jobId: string) => {
      setAndPersist((prev) => prev.filter((r) => r.jobId !== jobId))
    },
    [setAndPersist],
  )

  const trackRender = useCallback(
    (render: { jobId: string; mockId: string; mockTitle?: string }) => {
      if (!render.jobId || !render.mockId) return
      // Request notification permission now, while we still have the click
      // gesture from the render button (browsers require a gesture).
      if ('Notification' in window && Notification.permission === 'default') {
        try {
          Notification.requestPermission()
        } catch {
          // ignore
        }
      }
      setAndPersist((prev) => {
        if (prev.some((r) => r.jobId === render.jobId)) return prev
        return [
          ...prev,
          {
            jobId: render.jobId,
            mockId: render.mockId,
            mockTitle: render.mockTitle || 'Your mock',
            startedAt: Date.now(),
          },
        ]
      })
    },
    [setAndPersist],
  )

  useEffect(() => {
    let cancelled = false

    const pollOnce = async () => {
      const jobs = activeRef.current
      if (jobs.length === 0) return
      await Promise.all(
        jobs.map(async (job) => {
          if (inFlight.current.has(job.jobId)) return
          if (Date.now() - job.startedAt > MAX_TRACK_MS) {
            remove(job.jobId)
            return
          }
          inFlight.current.add(job.jobId)
          try {
            const status = await mockService.getJobStatus(job.jobId)
            if (cancelled) return
            if (status.status === 'finished') {
              const secs = status.actual_duration_seconds
              const body = secs
                ? `${job.mockTitle} is ready (rendered in ${Math.round(secs)}s).`
                : `${job.mockTitle} is ready.`
              toast.dismiss(PROGRESS_TOAST_ID)
              toast.success(`Render complete: ${job.mockTitle}`, {
                id: `render-done-${job.jobId}`,
                duration: 6000,
              })
              showBrowserNotification('Render complete', body)
              remove(job.jobId)
            } else if (status.status === 'failed') {
              const code = status.error_code ? ` (${status.error_code})` : ''
              const msg =
                status.error || 'Render failed. Check validation and assets, then try again.'
              toast.dismiss(PROGRESS_TOAST_ID)
              toast.error(`Render failed: ${job.mockTitle}${code}\n${msg}`, {
                id: `render-fail-${job.jobId}`,
                duration: 12000,
              })
              showBrowserNotification('Render failed', `${job.mockTitle}: ${msg}${code}`)
              remove(job.jobId)
            }
            // queued / started / unknown: keep polling until terminal or the
            // MAX_TRACK_MS safety valve above drops it.
          } catch {
            // transient error (e.g. network blip) — keep polling
          } finally {
            inFlight.current.delete(job.jobId)
          }
        }),
      )
    }

    // Poll immediately on mount so a job restored from localStorage after a
    // refresh is picked up right away, then on a steady interval.
    pollOnce()
    const interval = window.setInterval(pollOnce, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [remove])

  return (
    <RenderNotificationsContext.Provider value={{ trackRender, activeRenders }}>
      {children}
    </RenderNotificationsContext.Provider>
  )
}

export function useRenderNotifications(): RenderNotificationsValue {
  const ctx = useContext(RenderNotificationsContext)
  if (!ctx) {
    // No-op fallback so a component can call trackRender even if it is rendered
    // outside the provider (e.g. in an isolated unit test).
    return { trackRender: () => {}, activeRenders: [] }
  }
  return ctx
}
